import type { EntityFormConfig } from '@components/integrated/entity-form';
import {
  createFormActions,
  createFormEndpoint,
  createFormI18n,
  createFormCreateId,
  createFormEditId,
  createFormLayout,
  createFormWorkflow,
} from './formConfigHelpers';

/**
 * Returns the EntityForm configuration for User create/edit forms.
 *
 * @param mode - Whether the form is for creating or editing a user.
 * @param userId - The user ID (required in edit mode for fetching data).
 */
export function getUserFormConfig(
  mode: 'create' | 'edit',
  userId?: string,
): EntityFormConfig {
  const isEdit = mode === 'edit';

  return {
    id: isEdit ? createFormEditId('user', userId) : createFormCreateId('user'),

    endpoint: createFormEndpoint(
      isEdit,
      '/api/admin/users',
      `/api/admin/users/${userId}`,
      !!userId,
      ['name', 'email'],
    ),

    fields: {
      name: {
        type: 'text',
        label: 'Name',
        placeholder: 'e.g. John Doe',
        order: 1,
        section: 'basic',
        validation: {
          required: true,
          max: { value: 255, message: 'Name must be at most 255 characters' },
        },
      },
      email: {
        type: 'email',
        label: 'Email',
        placeholder: 'e.g. john@example.com',
        order: 2,
        section: 'basic',
        validation: {
          required: true,
          max: { value: 255, message: 'Email must be at most 255 characters' },
        },
      },
      password: {
        type: 'password',
        label: 'Password',
        placeholder: isEdit ? '********' : 'Enter a password',
        helperText: isEdit
          ? 'Leave blank to keep current password'
          : undefined,
        order: 3,
        section: 'basic',
        validation: {
          required: !isEdit,
          min: { value: 8, message: 'Password must be at least 8 characters' },
        },
      },
    },

    sections: [
      {
        id: 'basic',
        title: 'User Information',
        order: 1,
      },
    ],

    actions: createFormActions('User', isEdit, { alignment: 'right' }),

    layout: createFormLayout(),

    workflow: createFormWorkflow(),

    i18n: createFormI18n('User', isEdit),
  };
}
